import { useEffect, useState } from "react";
import { MdPerson, MdToggleOn, MdToggleOff, MdAdd, MdEdit, MdDelete, MdCheck, MdClose, MdHourglassEmpty, MdCardMembership } from "react-icons/md";
import Card from "components/card";
import { api } from "api";

const LICENSE_TYPES = [
  { value: "free", label: "Ucretsiz (Sinirsiz)" },
  { value: "trial", label: "Deneme (2 gun)" },
  { value: "demo", label: "Demo (14 gun)" },
  { value: "3month", label: "3 Ay" },
  { value: "6month", label: "6 Ay" },
  { value: "yearly", label: "1 Yil" },
  { value: "lifetime", label: "Suresiz" },
];

const LICENSE_LABELS: Record<string,string> = { free: "Ucretsiz", trial: "Deneme", demo: "Demo (eski)", "3month": "3 Ay", "6month": "6 Ay", yearly: "1 Yil", lifetime: "Suresiz" };

const Users = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [pending, setPending] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"pending" | "all">("pending");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", password: "", phone: "", company: "" });
  const [showEdit, setShowEdit] = useState<any>(null);
  const [editForm, setEditForm] = useState({ full_name: "", email: "", phone: "", company: "", password: "", email_verified: false, is_approved: false, is_active: true });
  const [showApprove, setShowApprove] = useState<any>(null);
  const [approveType, setApproveType] = useState("yearly");
  const [approveDevices, setApproveDevices] = useState(3);
  const [showExtend, setShowExtend] = useState<any>(null);
  const [extendType, setExtendType] = useState("yearly");
  const [extendMaxDevices, setExtendMaxDevices] = useState(3);
  const [extendLicense, setExtendLicense] = useState<any>(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.getUsers(), api.getPendingUsers()])
      .then(([u, p]) => { setUsers(u); setPending(p); })
      .catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleCreate = async () => {
    setError("");
    if (!form.full_name || !form.email || !form.password) { setError("Ad soyad, email ve sifre zorunludur"); return; }
    if (form.password.length < 8) { setError("Sifre en az 8 karakter olmali"); return; }
    try {
      await api.createUser(form);
      setShowCreate(false); load();
      setMsg("Kullanici olusturuldu!"); setTimeout(() => setMsg(""), 3000);
    } catch (e: any) { setError(e.message); }
  };

  const handleApprove = async () => {
    if (!showApprove) return;
    try {
      await api.approveUser(showApprove.id, { license_type: approveType, max_devices: approveDevices });
      setShowApprove(null); load();
      setMsg("Kullanici onaylandi ve lisans olusturuldu!"); setTimeout(() => setMsg(""), 3000);
    } catch (e: any) { alert(e.message); }
  };

  const handleReject = async (u: any) => {
    if (!window.confirm(`"${u.full_name}" reddedilecek ve silinecek. Emin misiniz?`)) return;
    try { await api.rejectUser(u.id); load(); setMsg("Kullanici reddedildi"); setTimeout(() => setMsg(""), 3000); }
    catch (e: any) { alert(e.message); }
  };

  const handleEditUser = async () => {
    setError("");
    if (!editForm.full_name || !editForm.email) { setError("Ad soyad ve email zorunludur"); return; }
    try {
      const data: any = {
        full_name: editForm.full_name,
        email: editForm.email,
        phone: editForm.phone,
        email_verified: editForm.email_verified,
        is_approved: editForm.is_approved,
        is_active: editForm.is_active,
      };
      if (editForm.password) data.password = editForm.password;
      await api.updateUser(showEdit.id, data);
      setShowEdit(null); load();
      setMsg("Kullanici guncellendi!"); setTimeout(() => setMsg(""), 3000);
    } catch (e: any) { setError(e.message); }
  };

  const handleDelete = async (u: any) => {
    if (!window.confirm(`"${u.full_name}" silinecek. Emin misiniz?`)) return;
    try { await api.deleteUser(u.id); load(); } catch (e: any) { alert(e.message); }
  };

  const handleToggle = async (id: number) => { await api.toggleUserActive(id); load(); };

  const handleExtend = async () => {
    if (!showExtend) return;
    try {
      // Lisans suresi degisikligi
      await api.extendLicense(showExtend.id, { license_type: extendType });
      // Maks cihaz sayisi degistiyse update et
      if (extendLicense && extendMaxDevices !== extendLicense.max_devices) {
        await api.updateLicense(extendLicense.id, { max_devices: extendMaxDevices });
      }
      setShowExtend(null); setExtendLicense(null); load();
      setMsg("Lisans guncellendi!"); setTimeout(() => setMsg(""), 3000);
    } catch (e: any) { alert(e.message); }
  };

  const loadLicenseDetail = async (userId: number) => {
    try {
      const licenses: any[] = await api.getLicenses();
      const active = licenses.find((l: any) => l.user_id === userId && l.is_active);
      if (!active) { setExtendLicense(null); setExtendMaxDevices(3); return; }
      const full = await api.getLicense(active.id);
      setExtendLicense(full);
      setExtendMaxDevices(full.max_devices || 3);
    } catch { setExtendLicense(null); }
  };

  const handleRemoveDevice = async (deviceId: number) => {
    if (!extendLicense) return;
    if (!window.confirm("Bu cihazi silmek istediginize emin misiniz? Kullanici tekrar giris yapabilir.")) return;
    try {
      await api.removeDevice(extendLicense.id, deviceId);
      await loadLicenseDetail(showExtend.id); // listeyi yenile
      setMsg("Cihaz silindi!"); setTimeout(() => setMsg(""), 2500);
    } catch (e: any) { alert(e.message); }
  };

  return (
    <div>
      {msg && <div className="mb-4 rounded-xl bg-green-50 p-3 text-sm text-green-700">{msg}</div>}

      <div className="mt-3 flex items-center justify-between">
        <div className="flex gap-2">
          <button onClick={() => setTab("pending")} className={`rounded-xl px-5 py-2 text-sm font-medium transition ${tab === "pending" ? "bg-brand-500 text-white" : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-navy-700 dark:text-white"}`}>
            <MdHourglassEmpty className="inline h-4 w-4 mr-1" /> Onay Bekleyenler ({pending.length})
          </button>
          <button onClick={() => setTab("all")} className={`rounded-xl px-5 py-2 text-sm font-medium transition ${tab === "all" ? "bg-brand-500 text-white" : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-navy-700 dark:text-white"}`}>
            Tum Kullanicilar ({users.length})
          </button>
        </div>
        <button onClick={() => { setForm({ full_name: "", email: "", password: "", phone: "", company: "" }); setError(""); setShowCreate(true); }}
          className="flex items-center gap-2 rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600 transition">
          <MdAdd className="h-5 w-5" /> Yeni Kullanici
        </button>
      </div>

      {/* ONAY BEKLEYENLER */}
      {tab === "pending" && (
        <Card extra="mt-4 p-4">
          {loading ? <p className="p-4 text-center text-gray-500">Yukleniyor...</p> : pending.length === 0 ? (
            <div className="p-8 text-center">
              <MdCheck className="mx-auto h-12 w-12 text-green-300 mb-3" />
              <p className="text-gray-500">Onay bekleyen kullanici yok</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pending.map(u => (
                <div key={u.id} className="flex items-center justify-between rounded-xl border-2 border-orange-200 bg-orange-50 px-5 py-4 dark:border-orange-800 dark:bg-orange-900/20">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100"><MdPerson className="h-6 w-6 text-orange-500" /></div>
                    <div>
                      <p className="font-bold text-navy-700 dark:text-white">{u.full_name}</p>
                      <p className="text-sm text-gray-600">{u.email}</p>
                      <div className="flex gap-3 mt-1 text-xs text-gray-500">
                        {u.phone && <span>Tel: {u.phone}</span>}
                        {u.company && <span>Sirket: {u.company}</span>}
                        <span>Kayit: {new Date(u.created_at).toLocaleDateString("tr-TR")}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setShowApprove(u); setApproveType("yearly"); setApproveDevices(3); }}
                      className="flex items-center gap-1 rounded-xl bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 transition">
                      <MdCheck className="h-5 w-5" /> Onayla
                    </button>
                    <button onClick={() => handleReject(u)}
                      className="flex items-center gap-1 rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 transition">
                      <MdClose className="h-5 w-5" /> Reddet
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* TUM KULLANICILAR */}
      {tab === "all" && (
        <Card extra="mt-4 p-4">
          {loading ? <p className="p-4 text-center text-gray-500">Yukleniyor...</p> : users.length === 0 ? (
            <p className="p-4 text-center text-gray-500">Henuz kayitli kullanici yok</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-navy-700">
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Kullanici</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Telefon</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Sirket</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Lisans</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Durum</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Islem</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700/50 transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-lightPrimary dark:bg-navy-700"><MdPerson className="h-5 w-5 text-brand-500" /></div>
                          <span className="font-medium text-navy-700 dark:text-white">{u.full_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{u.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{u.phone || "-"}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{u.company || "-"}</td>
                      <td className="px-4 py-3">
                        {u.license ? (
                          <div>
                            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${u.license.expired ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}>
                              {LICENSE_LABELS[u.license.type] || u.license.type}
                            </span>
                            {u.license.days_left !== null && (
                              <p className={`text-[10px] mt-0.5 ${u.license.expired ? "text-red-500" : u.license.days_left <= 3 ? "text-orange-500" : "text-gray-400"}`}>
                                {u.license.expired ? "Suresi doldu" : `${u.license.days_left} gun kaldi`}
                              </p>
                            )}
                            {u.license.days_left === null && <p className="text-[10px] text-gray-400 mt-0.5">Suresiz</p>}
                          </div>
                        ) : (
                          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">Yok</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {u.is_active ? <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-600">Aktif</span>
                          : <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-600">Pasif</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => { setShowExtend(u); setExtendType("yearly"); loadLicenseDetail(u.id); }} className="text-purple-500 hover:text-purple-700" title="Lisans / Cihaz Yonet"><MdCardMembership className="h-5 w-5" /></button>
                          <button onClick={() => { setEditForm({ full_name: u.full_name, email: u.email, phone: u.phone || "", company: u.company || "", password: "", email_verified: !!u.email_verified, is_approved: !!u.is_approved, is_active: u.is_active !== false }); setError(""); setShowEdit(u); }} className="text-brand-500 hover:text-brand-700"><MdEdit className="h-5 w-5" /></button>
                          <button onClick={() => handleToggle(u.id)} className={u.is_active ? "text-orange-400 hover:text-orange-600" : "text-green-400 hover:text-green-600"}>
                            {u.is_active ? <MdToggleOn className="h-6 w-6" /> : <MdToggleOff className="h-6 w-6" />}
                          </button>
                          <button onClick={() => handleDelete(u)} className="text-red-400 hover:text-red-600"><MdDelete className="h-5 w-5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ONAY MODALI */}
      {showApprove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowApprove(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-navy-800" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-2">Kullaniciyi Onayla</h3>
            <p className="text-sm text-gray-500 mb-4">{showApprove.full_name} ({showApprove.email})</p>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Lisans Suresi</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {LICENSE_TYPES.map(t => (
                    <button key={t.value} onClick={() => setApproveType(t.value)}
                      className={`rounded-xl py-2.5 text-sm font-medium transition ${approveType === t.value ? "bg-brand-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Max Cihaz Sayisi</label>
                <input type="number" min={1} max={10} value={approveDevices} onChange={e => setApproveDevices(Number(e.target.value))}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none" />
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setShowApprove(null)} className="rounded-xl px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100">Iptal</button>
              <button onClick={handleApprove} className="rounded-xl bg-green-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-green-600">Onayla ve Lisans Olustur</button>
            </div>
          </div>
        </div>
      )}

      {/* KULLANICI OLUSTUR */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowCreate(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-navy-800" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">Yeni Kullanici</h3>
            {error && <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
            <div className="space-y-3">
              <input type="text" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} placeholder="Ad Soyad *" className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none" />
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Email *" className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none" />
              <input type="text" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Sifre * (min 8)" className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none" />
              <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Telefon" className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none" />
              <input type="text" value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Sirket" className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none" />
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setShowCreate(false)} className="rounded-xl px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100">Iptal</button>
              <button onClick={handleCreate} className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600">Olustur</button>
            </div>
          </div>
        </div>
      )}

      {/* KULLANICI DUZENLE */}
      {showEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowEdit(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-navy-800" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">Kullanici Duzenle</h3>
            {error && <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
            <div className="space-y-3">
              <input type="text" value={editForm.full_name} onChange={e => setEditForm({...editForm, full_name: e.target.value})} placeholder="Ad Soyad *" className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none" />
              <input type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} placeholder="Email *" className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none" />
              <input type="tel" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} placeholder="Telefon" className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none" />
              <input type="text" value={editForm.company} onChange={e => setEditForm({...editForm, company: e.target.value})} placeholder="Sirket" className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none" />
              <div className="h-px bg-gray-200" />
              <input type="text" value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})} placeholder="Yeni sifre (bos birakirsan degismez)" className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none" />
              <div className="h-px bg-gray-200" />
              <div className="rounded-xl bg-gray-50 p-3 dark:bg-navy-700">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Hesap Durumu (Admin Kontrolü)</p>
                <label className="flex items-center gap-2 py-1 text-sm text-gray-700 dark:text-white cursor-pointer">
                  <input type="checkbox" checked={editForm.email_verified} onChange={e => setEditForm({...editForm, email_verified: e.target.checked})} className="h-4 w-4 accent-brand-500" />
                  Email doğrulanmış
                </label>
                <label className="flex items-center gap-2 py-1 text-sm text-gray-700 dark:text-white cursor-pointer">
                  <input type="checkbox" checked={editForm.is_approved} onChange={e => setEditForm({...editForm, is_approved: e.target.checked})} className="h-4 w-4 accent-brand-500" />
                  Admin tarafından onaylı
                </label>
                <label className="flex items-center gap-2 py-1 text-sm text-gray-700 dark:text-white cursor-pointer">
                  <input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm({...editForm, is_active: e.target.checked})} className="h-4 w-4 accent-brand-500" />
                  Hesap aktif
                </label>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setShowEdit(null)} className="rounded-xl px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100">Iptal</button>
              <button onClick={handleEditUser} className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600">Guncelle</button>
            </div>
          </div>
        </div>
      )}
      {/* LISANS UZATMA */}
      {showExtend && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8" onClick={() => { setShowExtend(null); setExtendLicense(null); }}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-navy-800 my-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-2">Lisans / Cihaz Yonetimi</h3>
            <p className="text-sm text-gray-500 mb-1">{showExtend.full_name}</p>
            <p className="text-sm text-gray-400 mb-4">{showExtend.email}</p>
            {showExtend.license && (
              <div className="mb-4 rounded-xl bg-gray-50 p-3 text-sm dark:bg-navy-700">
                <span className="text-gray-500">Mevcut: </span>
                <span className="font-medium">{LICENSE_LABELS[showExtend.license.type] || showExtend.license.type}</span>
                {showExtend.license.end_date && <span className="text-gray-400"> (bitis: {showExtend.license.end_date})</span>}
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Yeni Lisans Suresi</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {LICENSE_TYPES.map(t => (
                  <button key={t.value} onClick={() => setExtendType(t.value)}
                    className={`rounded-xl py-2.5 text-sm font-medium transition ${extendType === t.value ? "bg-brand-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-white"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Maks Cihaz Sayisi */}
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Maks. Cihaz Sayisi</label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={extendMaxDevices}
                  onChange={(e) => setExtendMaxDevices(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-24 rounded-xl border border-gray-200 p-2.5 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
                <span className="text-xs text-gray-400">
                  {extendLicense ? `Su an: ${extendLicense.devices?.length || 0} kayitli cihaz` : "Lisans yukleniyor..."}
                </span>
              </div>
            </div>

            {/* Kayitli Cihazlar */}
            {extendLicense && extendLicense.devices && extendLicense.devices.length > 0 && (
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Kayitli Cihazlar</label>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {extendLicense.devices.map((d: any) => (
                    <div key={d.id} className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 p-2.5 dark:border-navy-600">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-navy-700 dark:text-white truncate">
                          {d.device_name || d.device_id || "Cihaz"}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {d.platform || "—"} {d.last_login ? `• ${new Date(d.last_login).toLocaleDateString("tr-TR")}` : ""}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveDevice(d.id)}
                        className="rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100"
                        title="Cihazi sil"
                      >
                        Sil
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {extendLicense && (!extendLicense.devices || extendLicense.devices.length === 0) && (
              <p className="mt-3 text-xs text-gray-400">Henuz kayitli cihaz yok.</p>
            )}

            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => { setShowExtend(null); setExtendLicense(null); }} className="rounded-xl px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100">Iptal</button>
              <button onClick={handleExtend} className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
